import React from 'react'
import { Modal, Input, Label, Textarea, Button, Checkbox } from '@/shared/ui'
import { uploadPublicFile } from '@/modules/upload/services/uploadService'
import { upsertTestimonial, type Testimonial } from '../services/testimonialsService'
import InstagramPreviewModal from './InstagramPreviewModal'

type Props = {
  open: boolean
  onClose: () => void
  onSaved: () => void
  initial?: Partial<Testimonial>
}

export default function TestimonialFormModal({ open, onClose, onSaved, initial }: Props) {
  const [authorName, setAuthorName] = React.useState(initial?.author_name ?? '')
  const [authorHandle, setAuthorHandle] = React.useState(initial?.author_handle ?? '')
  const [text, setText] = React.useState(initial?.text ?? '')
  const [href, setHref] = React.useState(initial?.href ?? '')
  const [avatarUrl, setAvatarUrl] = React.useState(initial?.avatar_url ?? '')
  const [visible, setVisible] = React.useState<boolean>(initial?.visible ?? true)
  const [saving, setSaving] = React.useState(false)
  const [error, setError] = React.useState<string | null>(null)
  const [previewOpen, setPreviewOpen] = React.useState(false)

  React.useEffect(() => {
    if (!open) return
    setAuthorName(initial?.author_name ?? '')
    setAuthorHandle(initial?.author_handle ?? '')
    setText(initial?.text ?? '')
    setHref(initial?.href ?? '')
    setAvatarUrl(initial?.avatar_url ?? '')
    setVisible(initial?.visible ?? true)
    setSaving(false)
    setError(null)
  }, [open, initial])

  async function handleUpload(e: React.ChangeEvent<HTMLInputElement>) {
    const file = e.target.files?.[0]
    if (!file) return
    try {
      const { url } = await uploadPublicFile(file, 'avatars/testimonials')
      setAvatarUrl(url)
    } catch (e: any) {
      setError(e?.message ?? 'Falha no upload do avatar')
    }
  }

  async function onSubmit(e: React.FormEvent) {
    e.preventDefault()
    setError(null)
    try {
      setSaving(true)
      await upsertTestimonial({
        id: initial?.id,
        author_name: authorName.trim(),
        author_handle: authorHandle.trim() || null,
        avatar_url: avatarUrl || null,
        text: text.trim(),
        href: href.trim() || null,
        visible,
      })
      onSaved()
      onClose()
    } catch (err: any) {
      setError(err?.message ?? 'Falha ao salvar depoimento')
    } finally {
      setSaving(false)
    }
  }

  return (
    <Modal open={open} onOpenChange={(v)=>{ if(!v) onClose() }} className="p-6 sm:p-8">
      <div className="mb-4 text-xl font-semibold">{initial?.id ? 'Editar depoimento' : 'Novo depoimento'}</div>
      <form className="grid gap-4 sm:grid-cols-2" onSubmit={onSubmit}>
        {error && <div className="sm:col-span-2 rounded-md border border-destructive/30 bg-destructive/10 p-2 text-sm text-destructive-foreground">{error}</div>}
        <div className="space-y-1.5">
          <Label>Nome</Label>
          <Input value={authorName} onChange={(e)=> setAuthorName(e.target.value)} placeholder="Nome do autor" required />
        </div>
        <div className="space-y-1.5">
          <Label>Handle</Label>
          <div className="flex items-center gap-2">
            <Input value={authorHandle} onChange={(e)=> setAuthorHandle(e.target.value)} placeholder="@usuario (opcional)" />
            {authorHandle && (
              <Button type="button" variant="outline" onClick={()=> setPreviewOpen(true)}>Pré‑visualizar</Button>
            )}
          </div>
        </div>
        <div className="space-y-1.5 sm:col-span-2">
          <Label>Descrição</Label>
          <Textarea value={text} onChange={(e)=> setText(e.target.value)} placeholder="Mensagem do depoimento" rows={4} required />
        </div>
        <div className="space-y-1.5">
          <Label>Avatar</Label>
          <input type="file" accept="image/*" onChange={handleUpload} />
          {avatarUrl && <img src={avatarUrl} alt="avatar" className="mt-2 h-14 w-14 rounded-full object-cover" />}
        </div>
        <div className="space-y-1.5">
          <Label>Link (opcional)</Label>
          <Input value={href} onChange={(e)=> setHref(e.target.value)} placeholder="https://..." />
        </div>
        <div className="space-y-1.5">
          <Label>Visível</Label>
          <div className="flex items-center gap-2"><Checkbox checked={visible} onChange={(e)=> setVisible((e.target as HTMLInputElement).checked)} /> <span className="text-sm">Mostrar na home</span></div>
        </div>
        <div className="sm:col-span-2 mt-2 flex justify-end gap-2">
          <Button type="button" variant="outline" onClick={onClose}>Cancelar</Button>
          <Button type="submit" disabled={saving}>{saving ? 'Salvando...' : 'Salvar'}</Button>
        </div>
      </form>
      <InstagramPreviewModal open={previewOpen} onClose={()=> setPreviewOpen(false)} handle={authorHandle || ''} />
    </Modal>
  )
}
